import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  initialWebrtcState,
  CallProgressStatus,
  TransferStatus,
  CallSessionInfo,
  ConferenceState,
} from "./webrtc.types";
import { LoadState } from "../../constants/enums";
import { SipClientStatus, SipCallInfo } from "../../services/webrtc/sipClient";
import {
  fetchWebrtcPublicConfig,
  fetchWebrtcConfig,
  enableWebrtcExtension,
} from "./webrtcThunk";
import { IStoreState } from "../initialStoreState";

const webrtcSlice = createSlice({
  name: "webrtc",
  initialState: initialWebrtcState,
  reducers: {
    setSipStatus: (state, action: PayloadAction<SipClientStatus>) => {
      state.sipStatus = action.payload;
      if (action.payload === "registered") {
        state.registrationError = null;
      }
    },
    setCurrentCall: (state, action: PayloadAction<SipCallInfo | null>) => {
      state.currentCall = action.payload;
    },
    setCallProgress: (state, action: PayloadAction<CallProgressStatus>) => {
      state.callProgress = action.payload;
    },
    setIceState: (state, action: PayloadAction<string | null>) => {
      state.iceState = action.payload;
    },
    setIncomingCall: (state, action: PayloadAction<SipCallInfo | null>) => {
      state.incomingCall = action.payload;
    },
    setRegistrationError: (state, action: PayloadAction<string | null>) => {
      state.registrationError = action.payload;
    },
    setMicPermission: (
      state,
      action: PayloadAction<"prompt" | "granted" | "denied">
    ) => {
      state.micPermission = action.payload;
    },
    clearWebrtcError: (state) => {
      state.error = null;
    },
    clearEnableError: (state) => {
      state.enableError = null;
      state.enableLoading = LoadState.NotLoaded;
    },
    setTransferStatus: (state, action: PayloadAction<TransferStatus>) => {
      state.transferStatus = action.payload;
      if (action.payload === "idle") {
        state.transferError = null;
        state.attendedTransferTarget = null;
        state.consultCallInfo = null;
      }
    },
    setTransferError: (state, action: PayloadAction<string | null>) => {
      state.transferError = action.payload;
      state.transferStatus = "failed";
    },
    setAttendedTransferTarget: (state, action: PayloadAction<string | null>) => {
      state.attendedTransferTarget = action.payload;
    },
    setConsultCallInfo: (state, action: PayloadAction<SipCallInfo | null>) => {
      state.consultCallInfo = action.payload;
    },
    openDialerWithNumber: (state, action: PayloadAction<string>) => {
      state.dialerOpen = true;
      state.dialerNumber = action.payload;
    },
    clearDialerRequest: (state) => {
      state.dialerOpen = false;
      state.dialerNumber = "";
    },

    // --- Multi-call session management ---
    setSessions: (state, action: PayloadAction<Record<string, CallSessionInfo>>) => {
      state.sessions = action.payload;
    },
    setActiveSessionId: (state, action: PayloadAction<string | null>) => {
      state.activeSessionId = action.payload;
    },
    addSession: (state, action: PayloadAction<CallSessionInfo>) => {
      state.sessions[action.payload.id] = action.payload;
    },
    removeSession: (state, action: PayloadAction<string>) => {
      delete state.sessions[action.payload];
      if (state.activeSessionId === action.payload) {
        state.activeSessionId = null;
      }
    },
    updateSession: (
      state,
      action: PayloadAction<{ id: string; changes: Partial<CallSessionInfo> }>
    ) => {
      const { id, changes } = action.payload;
      if (state.sessions[id]) {
        state.sessions[id] = { ...state.sessions[id], ...changes };
      }
    },

    // --- Conference management ---
    setConference: (state, action: PayloadAction<ConferenceState>) => {
      state.conference = action.payload;
    },
    addConferenceParticipant: (state, action: PayloadAction<string>) => {
      if (!state.conference.participantIds.includes(action.payload)) {
        state.conference.participantIds.push(action.payload);
      }
    },
    removeConferenceParticipant: (state, action: PayloadAction<string>) => {
      state.conference.participantIds = state.conference.participantIds.filter(
        (id) => id !== action.payload
      );
    },
    endConference: (state) => {
      state.conference = { active: false, participantIds: [], startTime: null };
    },

    // --- Multi-call UX ---
    setSwapInProgress: (state, action: PayloadAction<boolean>) => {
      state.swapInProgress = action.payload;
    },
    setMultiCallError: (state, action: PayloadAction<string | null>) => {
      state.multiCallError = action.payload;
    },
    setWaitingCallId: (state, action: PayloadAction<string | null>) => {
      state.waitingCallId = action.payload;
    },

    resetWebrtcState: () => initialWebrtcState,
  },
  extraReducers: (builder) => {
    // Public config
    builder.addCase(fetchWebrtcPublicConfig.pending, (state) => {
      state.publicConfigLoading = LoadState.InProgress;
    });
    builder.addCase(fetchWebrtcPublicConfig.fulfilled, (state, action) => {
      state.publicConfig = action.payload.data;
      state.publicConfigLoading = LoadState.Loaded;
    });
    builder.addCase(fetchWebrtcPublicConfig.rejected, (state, action) => {
      state.publicConfigLoading = LoadState.Failed;
      state.error = action.payload?.message || "Failed to load WebRTC config";
    });

    // Full config
    builder.addCase(fetchWebrtcConfig.pending, (state) => {
      state.fullConfigLoading = LoadState.InProgress;
      state.error = null;
    });
    builder.addCase(fetchWebrtcConfig.fulfilled, (state, action) => {
      // Store everything except password
      const { password, ...rest } = action.payload.data;
      state.fullConfig = rest;
      state.fullConfigLoading = LoadState.Loaded;
    });
    builder.addCase(fetchWebrtcConfig.rejected, (state, action) => {
      state.fullConfigLoading = LoadState.Failed;
      state.error = action.payload?.message || "Failed to fetch WebRTC config";
    });

    // Enable extension
    builder.addCase(enableWebrtcExtension.pending, (state) => {
      state.enableLoading = LoadState.InProgress;
      state.enableError = null;
    });
    builder.addCase(enableWebrtcExtension.fulfilled, (state) => {
      state.enableLoading = LoadState.Loaded;
    });
    builder.addCase(enableWebrtcExtension.rejected, (state, action) => {
      state.enableLoading = LoadState.Failed;
      state.enableError = action.payload?.message || "Failed to enable WebRTC";
    });
  },
});

export const {
  setSipStatus,
  setCurrentCall,
  setCallProgress,
  setIceState,
  setIncomingCall,
  setRegistrationError,
  setMicPermission,
  clearWebrtcError,
  clearEnableError,
  setTransferStatus,
  setTransferError,
  setAttendedTransferTarget,
  setConsultCallInfo,
  openDialerWithNumber,
  clearDialerRequest,
  // Multi-call session management
  setSessions,
  setActiveSessionId,
  addSession,
  removeSession,
  updateSession,
  // Conference management
  setConference,
  addConferenceParticipant,
  removeConferenceParticipant,
  endConference,
  // Multi-call UX
  setSwapInProgress,
  setMultiCallError,
  setWaitingCallId,
  resetWebrtcState,
} = webrtcSlice.actions;

export const webrtcReducer = webrtcSlice.reducer;

// --- Backward-compatible selectors ---

export const selectCurrentCall = (state: IStoreState): SipCallInfo | null => {
  const { activeSessionId, sessions } = state.webrtc;
  if (!activeSessionId) return state.webrtc.currentCall;
  const session = sessions[activeSessionId];
  if (!session) return null;
  return {
    id: session.id,
    direction: session.direction,
    remoteIdentity: session.remoteIdentity,
    displayName: session.displayName,
    startTime: session.startTime,
    isOnHold: session.status === "held",
    isMuted: session.isMuted,
  };
};

export const selectIncomingCall = (state: IStoreState): SipCallInfo | null => {
  const { sessions } = state.webrtc;
  const ringing = Object.values(sessions).find((s) => s.status === "ringing");
  if (!ringing) return state.webrtc.incomingCall;
  return {
    id: ringing.id,
    direction: ringing.direction,
    remoteIdentity: ringing.remoteIdentity,
    displayName: ringing.displayName,
    startTime: ringing.startTime,
    isOnHold: false,
    isMuted: false,
  };
};

export const selectAllSessions = (state: IStoreState): CallSessionInfo[] =>
  Object.values(state.webrtc.sessions);

export const selectHeldSessions = (state: IStoreState): CallSessionInfo[] =>
  Object.values(state.webrtc.sessions).filter((s) => s.status === "held");

export const selectSessionCount = (state: IStoreState): number =>
  Object.keys(state.webrtc.sessions).length;

export const selectIsConferenceActive = (state: IStoreState): boolean =>
  state.webrtc.conference.active;
